import { sortBy } from "lodash-es";
import { defineStore } from "pinia";

import type { IdMap } from "@/model/baseTypes";
import type { BoardCard, SearchResult } from "@/model/card";
import type { CardSearchResult } from "@/model/search";

import { useBoardStore } from "../board";
import { useBoardsStore } from "../boards";

type Index = { index: number } | { id: string };

export const useSearchResultsStore = defineStore("searchResults", {
  state: () => {
    return {
      found: [] as CardSearchResult[],
      foundIndex: 0,
      backendSearching: false,
      backendResults: [] as SearchResult[],
    };
  },
  getters: {
    currentResult: (store) => store.found[store.foundIndex - 1],
    hasNoResults(): boolean {
      return this.found.length === 0 && this.hasNoBackendResults;
    },
    hasBackendResults: (store) => !store.backendSearching && store.backendResults.length > 0,
    hasNoBackendResults: (store) => !store.backendSearching && store.backendResults.length === 0,
  },
  actions: {
    search(cards: IdMap<BoardCard>, match: (card: BoardCard) => boolean) {
      const foundItemId = this.currentResult?.id;
      const found = new Array<CardSearchResult>();
      for (const id in cards) {
        if (match(cards[id])) {
          found.push(toSearchResult(cards[id]));
        }
      }
      this.found = sortSearchResults(found);
      const newFound = foundItemId
        ? this.found.findIndex((item) => item.id === foundItemId) + 1
        : 0;
      this.setCurrent({ index: newFound });
    },
    at(index: number) {
      return this.found[index - 1];
    },
    setCurrent(c: Index) {
      if (this.found.length > 0) {
        this.foundIndex = this.calcIndex(c);
      }
    },
    calcIndex(c: Index) {
      if ("index" in c) {
        return c.index;
      }
      const index = this.found.findIndex((result) => result.id === c.id);
      return index < 0 ? this.foundIndex : index === this.foundIndex - 1 ? 0 : index + 1;
    },
    unsetCurrent() {
      this.foundIndex = 0;
    },
  },
});

export function sortSearchResults<T extends SearchResult | CardSearchResult>(results: T[]): T[] {
  return sortBy(results, (r) => r.text.toLowerCase());
}

function toSearchResult(boardCard: BoardCard): CardSearchResult {
  const boardId = useBoardStore().boardId();
  const card = boardCard.data;
  return {
    boardId,
    id: card.id,
    text: card.text,
    typeId: card.type.id,
    iterationId: card.iterationId || undefined,
    teamId: card.teamId || undefined,
    precondTeamId: card.precondTeam?.id,
    dependTeamId: card.dependTeam?.id,
    flagType: card.flagType,
    almId: card.almId,
    card: boardCard,
    artId: useBoardsStore().boardById(boardId).artId,
    status: card.status,
    objectives: card.objectives,
  };
}
